'use client';

import {
  DataAccidentalWritten as data_ACCIDENTAL_WRITTEN,
  Note as note,
} from './main';
import { DataMode as data_MODE, MeasureTimemapEntry } from './main';
import { key } from './key';
import { Sample } from './Api';
import { Note } from './main';
import { safeWindow } from './browserUtils';

// Define types for our global objects
interface I18nInstance {
  language: string;
  [key: string]: any;
}

// Create safe getters that check for window existence
export const getI18n = (): I18nInstance | null => {
  try {
    if (typeof window !== 'undefined') {
      const i18nInstance = (window as any).i18n;
      if (i18nInstance) {
        return i18nInstance;
      } else {
        console.log('[i18n] i18n instance not found on window object');
      }
    } else {
      console.log('[i18n] Window not available (server-side rendering)');
    }
  } catch (error) {
    console.error('[i18n] Error accessing i18n:', error);
  }
  return null;
};

// Export a safe version of i18n that checks for window existence
export const i18n = new Proxy<I18nInstance>({} as I18nInstance, {
  get: (target, prop: string | symbol) => {
    const i18nInstance = getI18n();
    if (i18nInstance && typeof prop === 'string') {
      return i18nInstance[prop];
    }
    return undefined;
  },
});

type MeasureTimemap = MeasureTimemapEntry[];

export function createIndexesArray(startIndex: number, endIndex: number) {
  const activeMeasuresIndexes = new Array(endIndex - startIndex);
  for (let i = startIndex; i < endIndex; i++) {
    activeMeasuresIndexes.push(i);
  }
  return activeMeasuresIndexes;
}

export type I18nObject = { en: string, ru: string };

export function createI18nObj(): I18nObject {
  return {
    en: "",
    ru: "",
  };
}

export function setValueToi18nObj(obj: any, value: string) {
  const i18nInstance = getI18n();
  dpAssert(i18nInstance);
  obj[i18nInstance.language] = value;
}

export function rightLanguage(obj: any) {
  if (!obj) return '';

  // Handle race condition - if obj is an empty MobX observable, return empty string
  if (typeof obj === 'object' && Object.keys(obj).length === 0) {
    return '';
  }

  const lowerCaseCurrentLanguage = i18n.language?.toLocaleLowerCase() || '';

  // Try current language first
  if (i18n && i18n.language && obj[i18n.language]) {
    return obj[i18n.language];
  }

  if (lowerCaseCurrentLanguage.startsWith("en") && obj.en) {
    return obj.en;
  } else if (lowerCaseCurrentLanguage.startsWith("ru") && obj.ru) {
    return obj.ru;
  }

  // Fallback to English
  if (obj.en) {
    return obj.en;
  }

  // Fallback to Russian
  if (obj.ru) {
    return obj.ru;
  }

  // Last resort: return first available value
  const values = Object.values(obj);
  return values.length > 0 ? values[0] : '';
}

export function isAdminPage() {
  const pathname = safeWindow.location.getPathname();
  return pathname?.toLocaleLowerCase().includes('admin') || false;
}

export function isDebugPage() {
  const pathname = safeWindow.location.getPathname();
  return pathname.toLowerCase().includes('debug');
}

// check if path is 127.0.0.1 or localhost
export function isLocalhost() {
  const hostname = safeWindow.location.getHostname();
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

export function isValidIndex(array: any[], index: number) {
  return Number.isInteger(index) && index >= 0 && index < array.length;
}

export function getCategorizedSamplesSort(samples: Sample[]): {
  [category: string]: Sample[];
} {
  const groupedSamples = samples.reduce((acc: any, sample: Sample) => {
    const category = rightLanguage(sample.category);

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(sample);
    return acc;
  }, {});

  const sortedCategories = Object.keys(groupedSamples).sort();

  // Step 3: Prepare structured output in alphabetical order
  const sortedSamplesForUI: { [category: string]: Sample[] } = {};
  sortedCategories.forEach((category) => {
    sortedSamplesForUI[category] = groupedSamples[category].sort(
      (a: any, b: any) => {
        // Sort samples within each category by any desired property
        // Here we sort by _id as an example
        if (a._id < b._id) return -1;
        if (a._id > b._id) return 1;
        return 0;
      }
    );
  });

  return sortedSamplesForUI;
}

export function renderTextNearNote(
  measureId: string,
  note: Note,
  text: string,
  yCoord: number,
  xCoord: number,
  fontSize: number = 303,
  col: string = 'red'
) {
  const noteEl = document.getElementById(note.noteId);
  if (noteEl && note) {
    const color = col;

    // Create the <g> element
    var gElement = makeSVGEl('g', {
      class: 'fing',
      fill: color,
      stroke: color,
    });

    const use = noteEl.children[0].children[0];
    const x = parseInt((use.attributes as any).x.value) + xCoord;
    const y = parseInt((use.attributes as any).y.value) + yCoord;

    // Create the <text> element
    var textElement = makeSVGEl('text', {
      x: x,
      y: y,
      'text-anchor': 'middle',
      'font-size': '0px',
      fill: color,
      stroke: color,
    });

    // Create the inner <tspan> elements
    var tspanOuter = makeSVGEl('tspan', {
      id: 't1aexkgc',
      class: 'text',
      fill: color,
      stroke: color,
    });
    var tspanInner = makeSVGEl('tspan', {
      'font-size': `${fontSize}px`,
      fill: color,
      stroke: color,
    });
    tspanInner.textContent = text;
    gElement.style.filter = 'brightness(220%)';
    tspanOuter.appendChild(tspanInner);
    textElement.appendChild(tspanOuter);
    gElement.appendChild(textElement);

    if (measureId) {
      const measureEl = document.getElementById(measureId);
      if (measureEl) {
        measureEl.appendChild(gElement);
        return { parent: measureEl, child: gElement };
      }
    }
  }
  return null;
}

export function makeSVGEl(tag: any, attrs: any) {
  var el: HTMLElement = document.createElementNS(
    'http://www.w3.org/2000/svg',
    tag
  );
  for (var k in attrs) {
    el.setAttribute(k, attrs[k]);
  }
  return el;
}

export function getCategorizedSamples(samples: Sample[]): {
  [category: string]: Sample[];
} {
  return samples.reduce((acc: any, sample: Sample) => {
    const category = rightLanguage(sample.category);

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(sample);
    return acc;
  }, {});
}

export function removeExtension(filename: string) {
  const parts = filename.split('.'); // Split the file name by period ('.')

  // Remove the last word from the parts array
  parts.pop();
  // Concatenate the remaining parts into a string
  return parts.join('.');
}

export function getFirstNote(timemap: MeasureTimemap) {
  for (const measure of timemap) {
    for (const note of measure.notes) {
      return note;
    }
  }
  return undefined;
}

export function createFileFromArrayBuffer(
  arrayBuffer: ArrayBuffer,
  fileName: string,
  type: string
) {
  // Create a new File object
  const file = new File([arrayBuffer], fileName, { type: type });

  return file;
}
export function isIndexValid(array: any, index: number) {
  return index >= 0 && index < array.length;
}
export function getFirstSystemHeight(): number | undefined {
  // set height for cursor
  const systems = document.getElementsByClassName('system');
  if (systems.length > 0) {
    const system = systems[0];
    const systemRect = system!.getBoundingClientRect();
    return systemRect.height;
  } else {
    console.error("cannot set height for cursor, can't find first system");
  }
  return undefined;
}

export function getPossibleAlterName(name: string): string {
  // call WS function
  if (typeof window !== 'undefined' && (window as any).getPossibleAlterName) {
    return (window as any).getPossibleAlterName(name);
  }
  return name;
}

export function notesWithTheSameTime(note: note): note[] {
  const result = [];
  let nextNote: any = note.next;
  let prevNote: any = note.prev;
  while (prevNote && prevNote.midi.time === (note as any).midi.time) {
    result.push(prevNote);
    prevNote = prevNote.prev;
  }
  while (nextNote && nextNote.midi.time === (note as any).midi.time) {
    result.push(nextNote);
    nextNote = nextNote.next;
  }
  return result;
}

export function notesWithTheSameTimeAndPitch(note: note): note[] {
  const sameChordNotes = notesWithTheSameTime(note);
  const sameChordAndPitchNotes = [];
  for (const chordNote of sameChordNotes) {
    if ((chordNote as any).midi.pitch === (note as any).midi.pitch) {
      sameChordAndPitchNotes.push(chordNote);
    }
  }
  return sameChordAndPitchNotes;
}

export function inbetweenNotes(firstNote: note, secondNote: note): note[] {
  let curr: note | undefined | null = firstNote.next;
  let result = [];
  while (curr && curr != secondNote) {
    if (curr) result.push(curr);
    curr = curr?.next;
  }
  return result;
}

export function getAllPossibleNames(pitch: number): string[] {
  if (typeof window !== 'undefined' && (window as any).getAllPossibleNames) {
    const vec = (window as any).getAllPossibleNames(pitch);
    const result = emcVectorToJSArray(vec);
    return result;
  }
  return [];
}

export function emcVectorToJSArray(vec: any) {
  const result: any[] = [];
  try {
    // retrieve value from the vector
    for (var i = 0; i < vec.size(); i++) {
      result.push(vec.get(i));
    }
  } catch (err) {
    vec.delete();
  }
  vec.delete();
  return result;
}

export function getLegitName(note: note, measure: MeasureTimemapEntry): string {
  if (note.accid != 0) {
    return getNameBasedOnAccid(note);
  } else {
    const names = getAllPossibleNames((note as any).midi.pitch);
    if (names.length > 0) {
      const name = getNameBasedOnKey(names, measure);
      if (name) return name;
    }
  }
  return note.step;
}

export function getNameBasedOnAccid(note: note): string {
  const parentNames = getAllPossibleNames((note as any).midi.pitch);
  let parentName = parentNames[0];
  if (parentNames.length > 0) {
    if (note.accid != 0) {
      if (note.accid > 0) {
        const parentSharpN = parentNames.find((n) => n.includes('#'));
        if (parentSharpN) {
          parentName = parentSharpN;
        }
      } else if (note.accid < 0) {
        const parentBN = parentNames.find((n) => n.includes('b'));
        if (parentBN) {
          parentName = parentBN;
        }
      }
    } else {
    }
  }
  return parentName;
}

// determine if note is sharp or bemole based on keyFifths if its > 0 then it's sharp otherwise it's bemole
// TODO TO WS
export function getNameBasedOnKey(
  names: string[],
  measure: MeasureTimemapEntry
) {
  if (names.length === 0) {
    return undefined;
  }
  if (names.length === 1) {
    return names[0];
  }
  let currentAlterNamesInMeasure = ['#', 'b'];
  if (measure.keyFifths < 0) {
    currentAlterNamesInMeasure = ['b'];
  } else if (measure.keyFifths > 0) {
    currentAlterNamesInMeasure = ['#'];
  }
  let isNameLegit = false;
  for (let name of names) {
    for (let alterName of currentAlterNamesInMeasure) {
      if (name.includes(alterName)) {
        isNameLegit = true;
        break;
      }
    }
    if (isNameLegit) {
      return name;
    }
  }
  return names[0];
}

// check if note B for example in key (we have in key Bb or B#)
// TODO TO WS
export function nameInKey(name: string, measure: MeasureTimemapEntry) {
  const keyNotes = getKeyNotesArr(measure);
  if (keyNotes && keyNotes.includes(name)) {
    // if we have it in keys than keep altered pitch
    return true;
  }
  return false;
}

// TODO TO WS
export function getKeyNotesArr(
  measure: MeasureTimemapEntry
): string[] | undefined {
  let mode = measure.keyMode === data_MODE.MODE_major ? 'major' : undefined;
  if (measure.keyMode === data_MODE.MODE_minor) mode = 'minor';
  let fifth = measure.keyFifths;
  if (mode) {
    const keyArr: any = (key as any)[mode][fifth];
    return keyArr;
  }
  return undefined;
}
export function getAccidValueBasedOnKey(
  measure: MeasureTimemapEntry,
  note: Note
) {
  const noteName = note.step.toLowerCase();
  const keys = getKeyNotesArr(measure);
  let resultAccid = data_ACCIDENTAL_WRITTEN.ACCIDENTAL_WRITTEN_NONE;
  if (keys) {
    for (const k of keys) {
      if (k[0].toLowerCase() === noteName) {
        if (k[1].toLowerCase() === 'b') {
          resultAccid = data_ACCIDENTAL_WRITTEN.ACCIDENTAL_WRITTEN_f;
          break;
        } else if (k[1].toLowerCase() === '#') {
          resultAccid = data_ACCIDENTAL_WRITTEN.ACCIDENTAL_WRITTEN_s;
          break;
        }
      }
    }
  }
  return resultAccid;
}

// darpiano assert
// this is assertion that will throw error with callstack
export function dpAssert(condition: any, message?: string): asserts condition {
  if (!condition) {
    const error = new Error(message || 'Assertion failed');
    throw error;
  }
}
export async function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
export function generateRandomString(length: number) {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}
export function midiToScientificPitch(midiNote: number) {
  /* const pitchClassesEn = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B']; */
  const pitchClassesRu = [
    'До',
    'До#',
    'Ре',
    'Ре#',
    'Ми',
    'Фа',
    'Фа#',
    'Соль',
    'Соль#',
    'Ля',
    'Ля#',
    'Си',
  ];

  const octave = Math.floor(midiNote / 12) - 1;
  const pitchClass = pitchClassesRu[midiNote % 12];

  return `${pitchClass} ${octave}`;
}
